/**
 * Test A/B du parcours d'inscription (12/10/2026, enquête « fuite à l'inscription ») :
 * - `v1` = parcours d'avant (témoin) ;
 * - `v2` = /signup qui rappelle l'essai gratuit (0 € aujourd'hui, carte à l'étape 3,
 *   sans confirmation du mot de passe) + étape Infos courte (bouton plus haut,
 *   « comment nous avez-vous connu ? » facultatif, code promo replié).
 *
 * Tirage 50/50 à la 1re page du parcours, gardé dans le navigateur, et recopié dans
 * `users/{uid}.signupFlow` à la création du profil. À l'étape Infos, la version du profil
 * prime (même version sur un autre appareil, ex. retour par l'email de relance). Sans
 * aucune trace (compte d'avant le test, ou créé via la page de connexion), on dérive la
 * version de l'uid : rejouable à l'analyse (`pilotage/tunnel_inscription.py`).
 * Hachage FNV-1a, bit de poids faible : correct pour 2 groupes, pas pour 3 ou plus.
 * Forcer une version pour tester : `?flow=v1` ou `?flow=v2`.
 */
export type SignupFlow = 'v1' | 'v2';

const KEY = 'signupFlow';
let memo: SignupFlow | undefined;

const isFlow = (v: unknown): v is SignupFlow => v === 'v1' || v === 'v2';

/** FNV-1a 32 bits : `v2` si impair. Doit rester identique au script d'analyse. */
export function flowFromId(id: string): SignupFlow {
    /* eslint-disable no-bitwise -- hachage FNV-1a */
    let h = 0x811c9dc5;
    for (let i = 0; i < id.length; i += 1) {
        h ^= id.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    /* eslint-enable no-bitwise */
    return h % 2 === 1 ? 'v2' : 'v1';
}

function readStored(): SignupFlow | undefined {
    try {
        const v = localStorage.getItem(KEY);
        return isFlow(v) ? v : undefined;
    } catch {
        return undefined;
    }
}

function store(flow: SignupFlow) {
    try {
        localStorage.setItem(KEY, flow);
    } catch {
        /* navigation privée : la version tient le temps de la page (memo) */
    }
}

/** Version déjà attribuée à ce navigateur, sans en tirer une nouvelle (profil Firestore). */
export function storedSignupFlow(): SignupFlow | undefined {
    return memo ?? readStored();
}

/** Valeur lue dans un profil (`users/{uid}.signupFlow`), si valide. */
export function parseSignupFlow(v: unknown): SignupFlow | undefined {
    return isFlow(v) ? v : undefined;
}

/**
 * Version du parcours (tirée et mémorisée au premier appel). Priorité : `?flow=`, version du
 * profil (`saved`), version déjà tirée dans ce navigateur, uid, tirage au hasard.
 */
export function getSignupFlow({
    uid,
    saved,
    random = Math.random,
}: { uid?: string; saved?: SignupFlow; random?: () => number } = {}): SignupFlow {
    let forced: string | null = null;
    try {
        forced = new URLSearchParams(globalThis.location?.search ?? '').get('flow');
    } catch {
        forced = null;
    }
    if (isFlow(forced)) {
        memo = forced;
        store(forced);
        return forced;
    }
    if (saved) {
        memo = saved;
        store(saved);
        return saved;
    }
    if (memo) return memo;
    const stored = readStored();
    if (stored) {
        memo = stored;
        return stored;
    }
    let flow: SignupFlow;
    if (uid) flow = flowFromId(uid);
    else flow = random() < 0.5 ? 'v1' : 'v2';
    memo = flow;
    store(flow);
    return flow;
}

/** Réservé aux tests. */
export function resetSignupFlowForTests() {
    memo = undefined;
}
